import {
  Auth0Provider,
  Auth0ProviderOptions,
  User,
  useAuth0,
} from "@auth0/auth0-react";
import AuthContext, { AuthContextInterface } from "./context";
import {
  CustomerGroup,
  ReferralPayload,
  VinoUser,
  initialAuthState,
} from "./state";
import { DataItem, InsertData } from "../../../api/src/services/dynamodb";
import {
  ErrorNotifier,
  errorDataBuilder,
} from "../../../api/src/utils/error-notifier";
import {
  IGetAddresses,
  IGetStoredInstruments,
} from "./../../../api/src/services/vino-api-services";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { AuthResolver } from "./resolver";
import { DynamoDB } from "aws-sdk";
import contextLogger from "../../utils/context-logger";
import isBrowser from "../../utils/is-browser";
import { logError } from "../../utils/logger";
import { navigate } from "gatsby";
import { reducer } from "./reducer";
import { trackFavouriteEvent } from "../../utils/event-tracking";
import { useLocation } from "@reach/router";
import vinoFetch from "../../utils/vinoFetch";
import { isEqual } from "lodash";
import config from "../../../config.json";
import { CHECKOUT_INTENT_PATHNAME_KEY } from "../../utils/constants";

type AuthProviderProps = { children?: ReactNode };

const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const {
    error,
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect: auth0Login,
    logout: auth0Logout,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const [state, dispatch] = useReducer(
    process.env.GATSBY_VM_ENV !== "production"
      ? contextLogger(reducer)
      : reducer,
    initialAuthState
  );

  const location = useLocation();
  const triedSSORef = useRef(false);

  const login = useCallback(
    async (appState: { returnTo: string; email: string }) => {
      let url = `/auth/login/using-password?returnTo=${appState.returnTo}`;
      if (appState.email) url = `${url}&email=${appState.email}`;
      await auth0Login({
        appState,
        fragment: url,
      });
    },
    [auth0Login]
  );

  const loginWithCredentials = useCallback(
    async (appState: { returnTo: string }, autoLogin: string) => {
      const url = `/auth/login/using-password?returnTo=${appState.returnTo}&autoLogin=${autoLogin}`;
      await auth0Login({
        appState,
        fragment: url,
        // redirectUri: "http://localhost:8001"
      });
    },
    [auth0Login]
  );

  const join = useCallback(
    async (appState: { returnTo: string; email: string }) => {
      let url = `/auth/join?returnTo=${appState.returnTo}`;
      if (appState.email) url = `${url}&email=${appState.email}`;
      await auth0Login({
        appState,
        fragment: url,
      });
    },
    [auth0Login]
  );

  const loginWithOtherOptions = useCallback(
    async (appState: { returnTo: string; email: string }) => {
      let url = `/auth/login?returnTo=${appState.returnTo}`;
      if (appState.email) url = `${url}&email=${appState.email}`;
      await auth0Login({
        appState,
        fragment: url,
      });
    },
    [auth0Login]
  );

  const forgotPassword = useCallback(
    async (appState: { returnTo: string; email: string }) => {
      let url = `/auth/login/forgot-password?returnTo=${appState.returnTo}`;
      if (appState.email) url = `${url}&email=${appState.email}`;
      await auth0Login({
        appState,
        fragment: url,
      });
    },
    [auth0Login]
  );

  const logout = useCallback(() => {
    const userId =
      process.env.GATSBY_VM_MARKET +
      "-" +
      (state.user?.["https://www.vinomofo.com/app_metadata"]?.[
        `${process.env.GATSBY_VM_MARKET}_EXTERNAL_ID`
      ] || "");

    // After Auth0 logs out, come back to a local page that will kick off BC logout
    const postLogoutUrl = `${
      window.location.origin
    }/auth-callback?next=${encodeURIComponent(window.location.origin)}&logout=yes`;

    auth0Logout({ returnTo: postLogoutUrl });

    if (
      typeof window !== "undefined" &&
      typeof window.analytics !== "undefined"
    ) {
      window.analytics.track("User Logout", {
        category: "Auth",
        userId,
      });
    }
  }, [auth0Logout, state.user]);

  const getAccessToken = useCallback(():
    | Promise<string | undefined>
    | undefined => {
    if (isAuthenticated) {
      const useCache =
        localStorage.getItem("refreshAuthToken") === "no" ||
        localStorage.getItem("refreshAuthToken") === null;
      localStorage.setItem("refreshAuthToken", "no");
      return getAccessTokenSilently({
        ignoreCache: !useCache,
      });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const getTokenID = useCallback(async () => {
    if (isAuthenticated) {
      const claims = await getIdTokenClaims();
      const idToken = claims?.__raw;
      return idToken;
    }
  }, [isAuthenticated, getIdTokenClaims]);

  const fetchCustomerGroup = async (
    customerID?: number | string
  ): Promise<CustomerGroup> => {
    const { data } = await vinoFetch.get("/api/get-customer-group", {
      params: {
        customerID: customerID,
      },
    });
    return data;
  };

  const getAddressesByCustomerId = async ({
    customerID,
  }: IGetAddresses): Promise<any> => {
    const accessToken = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${accessToken}` };
    const { data } = await vinoFetch.get(`/api/get-addresses-by-customerid`, {
      headers,
      params: {
        customerID,
      },
    });
    return data;
  };

  const getStoredInstrumentsByCustomerId = async ({
    customerID,
    defaultOnly,
  }: IGetStoredInstruments): Promise<any> => {
    const accessToken = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${accessToken}` };
    const { data } = await vinoFetch.get(
      `/api/get-stored-instruments-by-customerid`,
      {
        headers,
        params: {
          customerID,
          defaultOnly,
        },
      }
    );
    return data;
  };

  const validateEmail = useCallback(async (email: string): Promise<boolean> => {
    try {
      const { exists } = await AuthResolver.validateEmail(email);
      return exists;
    } catch (error) {
      console.error("Email validation error:", error);
      return false;
    }
  }, []);

  const updateProfile = async (payload: {
    given_name: string;
    family_name: string;
    email: string;
    phone: string;
    birthdate: string;
  }): Promise<{ error?: Error }> => {
    const token = await getAccessTokenSilently();
    const tokenid = await getTokenID();

    return await AuthResolver.updateProfile(payload, token, tokenid);
  };

  const changeEmailStepTwo = async (payload: {
    email: string;
  }): Promise<{ error?: Error }> => {
    const token = await getAccessTokenSilently();

    return await AuthResolver.changeEmailStepTwo(payload, token);
  };

  const changeEmailStepThree = async (payload: {
    email: string;
    otp: string;
  }): Promise<{ error?: Error }> => {
    const token = await getAccessTokenSilently();

    const response = await AuthResolver.changeEmailStepThree(payload, token);

    if (!response?.error && state.user?.email !== payload.email) {
      dispatch({
        type: "SET_USER",
        user: { ...state.user, email: payload.email },
      });
    }
    await getAccessTokenSilently({ ignoreCache: true });

    return response;
  };

  const hasSocialLogin = async (email: string): Promise<boolean> => {
    const { isSocial } = await AuthResolver.hasSocialLogin(email);

    return isSocial;
  };

  const setBigCommerceId = async (user: User): Promise<any> => {
    const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
    const customer_bigcommerce_id =
      userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];
    if (!customer_bigcommerce_id && user?.email) {
      // This is because the auth0 functions are still creating the customer in the background. So get it straight from BigCommerce
      try {
        const bcCustomer = await vinoFetch.get(
          "/api/get-customer-id-by-email",
          {
            params: {
              email: user.email,
              first_name: user.given_name,
              last_name: user.family_name,
            },
          }
        );
        return bcCustomer?.data[0]?.id;
      } catch (error) {
        return 0;
      }
    } else {
      return customer_bigcommerce_id;
    }
  };

  const rePopulateUser = async (user: VinoUser) => {
    const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
    const customer_loyalty_tier =
      userAppMeta?.[`LOYALTY_TIER_${process.env.GATSBY_VM_MARKET}`];
    if (user)
      dispatch({
        type: "SET_USER",
        user: { ...user, loyalty_tier: customer_loyalty_tier || "GREEN" },
      });
    // setAuthUser({ ...user, loyalty_tier: customer_loyalty_tier || "GREEN" });
  };

  const checkReferralCode = async ({
    bigcommerceId,
    hubspotId,
  }: ReferralPayload): Promise<any> => {
    dispatch({ type: "SETTING_REFERRAL_CODE" });
    try {
      const accessToken = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${accessToken}` };
      // Check for referral code in customer attribute
      const {
        data: { code: referralCode },
      } = await vinoFetch.get("/api/check-referral-code", {
        headers,
        params: {
          bigcommerceId,
          hubspotId,
        },
      });
      if (referralCode) {
        dispatch({
          type: "SET_REFERRAL_CODE",
          customerReferralCode: referralCode,
        });
        // setReferralCode(referralCode);
        // setCheckingReferralCode(false);
        return referralCode;
      } else {
        dispatch({
          type: "SET_REFERRAL_CODE",
          customerReferralCode: "",
        });
        // setCheckingReferralCode(false);
        return "";
      }
    } catch (error) {
      logError(error.message, { error });
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      dispatch({
        type: "SET_REFERRAL_CODE",
        customerReferralCode: "",
      });
      // setCheckingReferralCode(false);
      return "";
    }
  };

  const getFavouriteUserId = (): string | null => {
    return isAuthenticated && user?.email
      ? user.email
      : localStorage.getItem("favouriteId");
  };

  const fetchFavourites = async (
    lastEvaluatedID?: string
  ): Promise<DynamoDB.DocumentClient.ScanOutput> => {
    dispatch({ type: "SETTING_FAVOURITES" });
    // setFetchingFavourites(true);
    const favouriteUserId = getFavouriteUserId();

    if (!favouriteUserId) {
      dispatch({ type: "SET_FAVOURITES", favourites: [] });
      // setFetchingFavourites(false);
      return { Items: [], Count: 0 };
    }

    try {
      const accessToken = await getAccessToken();
      const headers =
        isAuthenticated && accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : {};
      const { data } = await vinoFetch.get("/api/get-favourite-items", {
        headers,
        params: {
          user_id: favouriteUserId,
          lastEvaluatedKey: lastEvaluatedID || undefined,
        },
      });

      if (data) {
        const doUpdate =
          JSON.stringify(state.favourites) !== JSON.stringify(data.Items) ||
          state.favourites?.length === data.Items?.length;

        if (doUpdate)
          dispatch({
            type: "SET_FAVOURITES",
            favourites: data.Items,
          });
        // if (isChanged) setFavourites(data.Items);
      }
      // setFetchingFavourites(false);
      return data;
    } catch (error) {
      logError(error.message, { error });
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      dispatch({
        type: "SET_FAVOURITES",
        favourites: [],
      });
      // setFetchingFavourites(false);
      return { Items: [], Count: 0 };
    }
  };

  const addToFavourites = async (
    data: InsertData
  ): Promise<DataItem | undefined> => {
    const { user_id, ...rest } = data;
    if (user_id) {
      try {
        const accessToken = await getAccessToken();
        const headers =
          isAuthenticated && accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {};

        const { data: newFavourite } = await vinoFetch.post(
          "/api/create-or-delete-favourite-item",
          data,
          {
            headers,
          }
        );
        if (newFavourite) {
          dispatch({
            type: "SET_FAVOURITES",
            favourites:
              state.favourites !== undefined
                ? [...state.favourites, newFavourite]
                : [newFavourite],
          });
          trackFavouriteEvent("Added to Favourites", {
            offer_name: newFavourite.offer_name,
            sku_code: newFavourite.sku_code,
            wine_name: newFavourite.wine_name,
          });
          // setFavourites(
          //   favourites !== undefined
          //     ? [...favourites, newFavourite]
          //     : [newFavourite]
          // );
          // await fetchFavourites();
        }

        return newFavourite;
      } catch (error) {
        logError(error.message, { error });
        const statusCode = error.response ? error.response.status : 400;
        ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
        dispatch({
          type: "SET_FAVOURITES",
          favourites: [],
        });
        // setFetchingFavourites(false);
        return undefined;
      }
    }
    //else login({ returnTo: location.pathname });
  };

  const removeToFavourites = async (
    id: string
  ): Promise<{ message: string } | undefined> => {
    try {
      const accessToken = await getAccessToken();
      const headers =
        isAuthenticated && accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : {};

      const { data } = await vinoFetch.post(
        "/api/create-or-delete-favourite-item",
        { id },
        {
          headers,
        }
      );
      if (data && id) {
        dispatch({
          type: "SET_FAVOURITES",
          favourites: state.favourites?.filter(
            (fav: DataItem) => !(fav.id === id)
          ),
        });
        const removed = state.favourites?.find(
          (fav: DataItem) => fav.id === id
        );
        trackFavouriteEvent("Removed from Favourites", {
          offer_name: removed.offer_name,
          sku_code: removed.sku_code,
          wine_name: removed.wine_name,
        });
        // setFavourites(favourites?.filter((fav) => !(fav.id === id)));
        // await fetchFavourites();
      }

      return data;
    } catch (error) {
      logError(error.message, { error });
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      dispatch({
        type: "SET_FAVOURITES",
        favourites: [],
      });
      // setFetchingFavourites(false);
      return undefined;
    }
  };

  const emailExistInAdmin = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        const accessToken = await getAccessToken();

        const headers =
          isAuthenticated && accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {};

        const { data } = await vinoFetch.post(
          "/api/get-user-metadata",
          { email },
          { headers }
        );

        return Object.keys(data).length !== 0;
      } catch (error) {
        console.error("Admin email check failed:", error);
        return false;
      }
    },
    [isAuthenticated]
  );

  const createUserInAdmin = useCallback(
    async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }): Promise<{ status: number; message: string }> => {
      try {
        const accessToken = await getAccessToken();

        const headers =
          isAuthenticated && accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {};

        const { data } = await vinoFetch.post(
          "/api/unlock-paywall",
          { email, password },
          { headers }
        );
        if (data.status === 200 || data.status === 409) {
          dispatch({ type: "UNLOCKED_PAYWALL" });
        }

        return data;
      } catch (error) {
        console.error("Admin user creation failed:", error);
        return { status: 500, message: "Internal Server Error" };
      }
    },
    [isAuthenticated]
  );

  // useEffect(() => {
  //   dispatch({
  //     type: "SET_AUTH0_STATE",
  //     isLoading: isLoading,
  //     isAuthenticated: isAuthenticated,
  //     error: error,
  //   });
  // }, [error, isAuthenticated, isLoading]);

  useEffect(() => {
    if (isLoading || triedSSORef.current) return;

    const params = new URLSearchParams(location.search || "");
    const sso = params.get("sso");

    if (sso) {
      params.delete("sso");
      const newSearch = params.toString();
      const cleaned =
        (location.pathname || "/") +
        (newSearch ? `?${newSearch}` : "") +
        (location.hash || "");

      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", cleaned);
      }
    }
    if (isAuthenticated || !sso) return;

    triedSSORef.current = true;
    (async () => {
      try {
        await getAccessTokenSilently();
      } catch (e: any) {
        const msg = e?.error || e?.message || "";
        if (/login_required|consent_required|interaction_required/i.test(msg)) {
          login({ returnTo: location.pathname, email: "" });
        } else {
          console.warn("Silent token failed:", e);
        }
      }
    })();
  }, [
    isAuthenticated,
    isLoading,
    location.pathname,
    location.search,
    location.hash,
    getAccessTokenSilently,
    auth0Login,
  ]);

  useEffect(() => {
    if (user) {
      if (isAuthenticated && !isLoading)
        dispatch({
          type: "SET_USER",
          user: user,
        });

      if (!state.user) {
        const MARKET = process.env.GATSBY_VM_MARKET;
        const APP_META_KEY = "https://www.vinomofo.com/app_metadata";
        const userAppMeta = user?.[APP_META_KEY];
        const customer_loyalty_tier = userAppMeta?.[`LOYALTY_TIER_${MARKET}`];
        const customer_bigcommerce_id =
          userAppMeta?.[`BIGCOMMERCE_${MARKET}_CUSTOMER_ID`];
        const customer_hubspot_id =
          userAppMeta?.[`HUBSPOT_${MARKET}_CONTACT_ID`];

        const chargebeeId = userAppMeta?.[`CHARGEBEE_${MARKET}_CUSTOMER_ID`];

        const attachCustomerGroupAddressesAndCard = async () => {
          const [group, addresses, cards] = await Promise.all([
            fetchCustomerGroup(customer_bigcommerce_id),
            getAddressesByCustomerId({ customerID: customer_bigcommerce_id }),
            getStoredInstrumentsByCustomerId({
              customerID: customer_bigcommerce_id,
              defaultOnly: true,
            }),
          ]);

          const defaultCard = cards.find((card) => card.is_default);
          let newProfile = {
            ...user,
            customer_group: group,
            addresses: addresses,
            stored_instruments: defaultCard ? [defaultCard] : [cards[0]],
          };

          if (group?.name !== customer_loyalty_tier) {
            newProfile = {
              ...newProfile,
              // [APP_META_KEY]: { ...userAppMeta, LOYALTY_TIER_AU: group.name },
            };
          }

          // CHARGEBEE
          if (!chargebeeId) {
            const tokenid = await getTokenID();
            const accessToken = await getAccessToken();
            if (tokenid && accessToken) {
              const chargebeeCustomer = await AuthResolver.getChargebeeCustomerDetails(
                tokenid
              ); // get chargebee customer details

              if (chargebeeCustomer?.id) {
                newProfile = {
                  ...newProfile,
                  [APP_META_KEY]: {
                    ...userAppMeta,
                    [`CHARGEBEE_${MARKET}_CUSTOMER_ID`]: chargebeeCustomer.id,
                  }, // update local state with chargebee customer id
                };
                const newAppMeta = newProfile[APP_META_KEY];

                const latestUserData = await AuthResolver.getLatestUserData(
                  accessToken
                ); // get the latest auth0 user details to compare and prevent multiple update requests
                const latestAppMeta = latestUserData?.app_metadata;

                if (!isEqual(latestAppMeta, newAppMeta)) {
                  const updatedUser = await AuthResolver.updateAppMetadata(
                    newAppMeta,
                    tokenid
                  ); // update metadata
                  await getAccessTokenSilently();
                  console.log("TEST PROVIDER updatedUser => ", updatedUser);
                }
              }
            }
          }

          dispatch({
            type: "SET_USER",
            user: newProfile as VinoUser,
          });
          // setAuthUser(newProfile as VinoUser);
          await fetchFavourites();
        };

        if (customer_bigcommerce_id) {
          if (
            !user?.customer_group &&
            !user?.addresses &&
            !user?.stored_instruments
          )
            attachCustomerGroupAddressesAndCard();

          if (!state.customerReferralCode)
            checkReferralCode({
              bigcommerceId: customer_bigcommerce_id,
              hubspotId: customer_hubspot_id,
            });
        }
      }
    } else {
      if (!isAuthenticated && !isLoading) fetchFavourites();
    }
  }, [user, isAuthenticated, isLoading]);

  const MAX_TIMEOUT_MS = 2_147_483_647; // ~24.8 days
  const timerRef = useRef<number | null>(null);
  const chunkRef = useRef<number | null>(null);
  const visibilityRef = useRef<(() => void) | null>(null);
  const focusRef = useRef<(() => void) | null>(null);
  const wakeDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    const safePathname =
      typeof window !== "undefined" ? window.location.pathname : "/";

    // Calculate buffer dynamically based on token lifetime
    const calcBuffer = (expirySeconds: number) => {
      const tenPercent = expirySeconds * 0.1;
      const minBaseBuffer = Math.min(tenPercent, 86_400);
      return minBaseBuffer;
    };

    const clearAll = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (chunkRef.current) {
        window.clearTimeout(chunkRef.current);
        chunkRef.current = null;
      }
      if (wakeDebounceRef.current) {
        window.clearTimeout(wakeDebounceRef.current);
        wakeDebounceRef.current = null;
      }
    };

    const schedule = (ms: number, fn: () => void) => {
      if (ms > MAX_TIMEOUT_MS) {
        // Break into safe chunks to avoid browser setTimeout limits
        chunkRef.current = (window.setTimeout(() => {
          if (!cancelled) schedule(ms - MAX_TIMEOUT_MS, fn);
        }, MAX_TIMEOUT_MS) as unknown) as number;
      } else {
        timerRef.current = (window.setTimeout(fn, ms) as unknown) as number;
      }
    };

    const scheduleTokenRefresh = async (): Promise<void> => {
      try {
        const res: any = await getAccessTokenSilently({
          detailedResponse: true,
        });
        const bufferBeforeExpiry = calcBuffer(res?.expires_in ?? 60);
        const msUntilExpiry = Math.max(0, (res?.expires_in ?? 60) * 1000);
        const fireIn = msUntilExpiry - bufferBeforeExpiry * 1000;

        if (cancelled) return;
        clearAll();

        // If already within the buffer, refresh immediately
        if (fireIn <= 0) {
          try {
            await getAccessTokenSilently();
          } catch {
            await login({ returnTo: safePathname, email: "" });
            return;
          }
          if (!cancelled) scheduleTokenRefresh();
          return;
        }

        // Otherwise schedule refresh for (expiry - buffer)
        schedule(fireIn, async () => {
          try {
            await getAccessTokenSilently();
          } catch {
            await login({ returnTo: safePathname, email: "" });
            return;
          }
          if (!cancelled) scheduleTokenRefresh();
        });
      } catch {
        await login({ returnTo: safePathname, email: "" });
      }
    };

    // Trigger on mount
    scheduleTokenRefresh();

    // Wake after long inactivity (debounced)
    const wake = () => {
      if (wakeDebounceRef.current) window.clearTimeout(wakeDebounceRef.current);
      wakeDebounceRef.current = (window.setTimeout(() => {
        scheduleTokenRefresh();
      }, 250) as unknown) as number; // debounce 250ms
    };

    if (typeof document !== "undefined") {
      visibilityRef.current = () => {
        if (document.visibilityState === "visible") wake();
      };
      document.addEventListener("visibilitychange", visibilityRef.current);
    }

    if (typeof window !== "undefined") {
      focusRef.current = () => wake();
      window.addEventListener("focus", focusRef.current);
    }

    return () => {
      cancelled = true;
      clearAll();
      if (visibilityRef.current)
        document.removeEventListener("visibilitychange", visibilityRef.current);
      if (focusRef.current)
        window.removeEventListener("focus", focusRef.current);
    };
  }, [getAccessTokenSilently, isAuthenticated, login]);

  // const context: AuthContextInterface = {
  //   error,
  //   user: authUser ? authUser : user,
  //   isAuthenticated,
  //   isLoading,
  //   join,
  //   login,
  //   logout,
  //   getAccessToken,
  //   getTokenID,
  //   validateEmail,
  //   updateProfile,
  //   changeEmailStepTwo,
  //   changeEmailStepThree,
  //   rePopulateUser,
  //   customerReferralCode: referralCode,
  //   checkingReferralCode,
  //   checkReferralCode,
  //   hasSocialLogin,
  //   favourites,
  //   fetchingFavourites,
  //   fetchFavourites,
  //   addToFavourites,
  //   removeToFavourites,
  // };

  const ctx: AuthContextInterface = useMemo(
    () => ({
      ...state,
      user: state?.user ? state.user : user,
      isAuthenticated,
      isLoading,
      error,
      join,
      login,
      logout,
      getAccessToken,
      getTokenID,
      validateEmail,
      emailExistInAdmin,
      createUserInAdmin,
      updateProfile,
      changeEmailStepTwo,
      changeEmailStepThree,
      rePopulateUser,
      checkReferralCode,
      hasSocialLogin,
      fetchFavourites,
      addToFavourites,
      removeToFavourites,
      loginWithCredentials,
      loginWithOtherOptions,
      forgotPassword,
    }),
    [
      state,
      join,
      login,
      logout,
      getAccessToken,
      getTokenID,
      validateEmail,
      emailExistInAdmin,
      createUserInAdmin,
      updateProfile,
      changeEmailStepTwo,
      changeEmailStepThree,
      rePopulateUser,
      checkReferralCode,
      hasSocialLogin,
      fetchFavourites,
      addToFavourites,
      removeToFavourites,
      loginWithCredentials,
      loginWithOtherOptions,
      forgotPassword,
    ]
  );

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
};

const onRedirectCallback = (appState) => {
  if (
    ["/guest-checkout", "?redirectToCheckout"].some((s) =>
      (appState?.returnTo ?? "").includes(s)
    )
  ) {
    localStorage.setItem("redirect_post_login_success", "true");
    try {
      const returnTo = appState?.returnTo as string;
      const u = new URL(returnTo, window.location.origin);
      localStorage.setItem(CHECKOUT_INTENT_PATHNAME_KEY, u.pathname || "/");
    } catch {
      localStorage.removeItem(CHECKOUT_INTENT_PATHNAME_KEY);
    }
    const timestamp = Date.now();
    const url = new URL(window.location.href);
    url.searchParams.set("redirection_ts", String(timestamp));
    window.history.replaceState({}, "", url.toString());
  } else {
    localStorage.setItem("redirect_post_login_success", "false");
    navigate(appState?.returnTo || "/");
  }
};

const AuthWrapper = ({ children }: AuthProviderProps): JSX.Element => {
  const location = isBrowser() ? useLocation() : { origin: "" };

  const auth0Config: Auth0ProviderOptions = {
    domain: process.env.GATSBY_AUTH0_DOMAIN,
    tenantDomain: process.env.GATSBY_AUTH0_TENANT_DOMAIN,
    clientId: process.env.GATSBY_AUTH0_CLIENT_ID,
    audience: config?.AUTH0_AUDIENCE,
    redirectUri: `${location.origin}/auth-callback`,
    cacheLocation: "localstorage",
    useRefreshTokens: true,
    onRedirectCallback,
  };

  return (
    <Auth0Provider {...auth0Config}>
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
};

export default AuthWrapper;
